/**
 * AIF-01 — Add / Edit version dialog (§11.2). Create captures versionNumber
 * (optional — default = next), versionName, releaseDate, note, artifactUri,
 * active; Edit omits versionNumber (the natural key is immutable). On create
 * the default version number = highest existing + 1.
 */
import { useRef, useState, type ReactElement } from "react";
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  LinearProgress,
  Stack,
  Switch,
  TextField,
  Typography,
} from "@mui/material";
import type { AiFindingModelVersion } from "@aeriosiq/types";
import {
  useCreateAiModelVersionMutation,
  useMintAiModelArtifactUploadUrlMutation,
  useUpdateAiModelVersionMutation,
} from "./aiModelQueries.js";
import { uploadArtifactToBlob } from "./uploadArtifactToBlob.js";
import { conflictMessage, errStatus } from "./apiError.js";
import { FileUpIcon } from "@aeriosiq/ui/icons";

/** Last path segment of an artifact URI, for the "current file" hint. */
function artifactFileName(uri: string): string {
  const noQuery = uri.split("?")[0] ?? uri;
  const last = noQuery.split("/").filter(Boolean).pop() ?? noQuery;
  try {
    return decodeURIComponent(last);
  } catch {
    return last;
  }
}

export interface VersionDialogProps {
  open: boolean;
  modelId: string;
  /** Edit mode when set; create mode otherwise. */
  version?: AiFindingModelVersion | null;
  /** Default version number for create mode (= max existing + 1). */
  nextVersionNumber: number;
  onClose: () => void;
  onSaved: () => void;
}

/** ISO date-time → `yyyy-mm-dd` for the date input (or "" if absent). */
function toDateInput(iso: string | null | undefined): string {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  return d.toISOString().slice(0, 10);
}

/** `yyyy-mm-dd` → ISO date-time at midnight UTC (or null). */
function fromDateInput(v: string): string | null {
  if (v.trim().length === 0) return null;
  return new Date(`${v}T00:00:00.000Z`).toISOString();
}

export function VersionDialog({
  open,
  modelId,
  version,
  nextVersionNumber,
  onClose,
  onSaved,
}: VersionDialogProps): ReactElement {
  const isEdit = version != null;
  const [versionNumber, setVersionNumber] = useState<string>(
    isEdit ? String(version.versionNumber) : String(nextVersionNumber),
  );
  const [versionName, setVersionName] = useState(version?.versionName ?? "");
  const [releaseDate, setReleaseDate] = useState(
    toDateInput(version?.releaseDate),
  );
  const [note, setNote] = useState(version?.note ?? "");
  const [artifactUri, setArtifactUri] = useState(version?.artifactUri ?? "");
  const [isActive, setIsActive] = useState(version?.isActive ?? true);

  // --- weights-file upload (R0627-08, edit mode only) ----------------------
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [uploadPct, setUploadPct] = useState<number | null>(null);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [uploadedName, setUploadedName] = useState<string | null>(null);
  const mintUpload = useMintAiModelArtifactUploadUrlMutation(modelId);
  const uploading = uploadPct !== null;

  const create = useCreateAiModelVersionMutation(modelId);
  const update = useUpdateAiModelVersionMutation(modelId);
  const pending = create.isPending || update.isPending;

  async function handleFilePicked(file: File): Promise<void> {
    if (!isEdit) return;
    setUploadError(null);
    setUploadedName(null);
    setUploadPct(0);
    try {
      const target = await mintUpload.mutateAsync(version.versionNumber);
      const result = await uploadArtifactToBlob({
        target,
        file,
        onProgress: (pct) => {
          setUploadPct(pct);
        },
      });
      // Persist the blob URL into the version's artifactUri (PATCH).
      await update.mutateAsync({
        versionNumber: version.versionNumber,
        body: { artifactUri: result.artifactUri },
      });
      setArtifactUri(result.artifactUri);
      setUploadedName(result.fileName);
      setUploadPct(null);
    } catch (err) {
      setUploadError(
        err instanceof Error
          ? `Upload failed: ${err.message}`
          : "Upload failed.",
      );
      setUploadPct(null);
    }
  }

  const numberValid =
    isEdit ||
    (versionNumber.trim().length > 0 &&
      Number.isInteger(Number(versionNumber)) &&
      Number(versionNumber) >= 1);
  const canSubmit = numberValid && !pending && !uploading;

  function handleSubmit(): void {
    if (!canSubmit) return;
    const common = {
      versionName: versionName.trim().length > 0 ? versionName.trim() : null,
      releaseDate: fromDateInput(releaseDate),
      note: note.trim().length > 0 ? note.trim() : null,
      artifactUri: artifactUri.trim().length > 0 ? artifactUri.trim() : null,
      isActive,
    };
    if (isEdit) {
      update.mutate(
        { versionNumber: version.versionNumber, body: common },
        {
          onSuccess: () => {
            onSaved();
          },
        },
      );
    } else {
      create.mutate(
        { versionNumber: Number(versionNumber), ...common },
        {
          onSuccess: () => {
            onSaved();
          },
        },
      );
    }
  }

  const mut = isEdit ? update : create;
  const errorMsg = mut.isError
    ? errStatus(mut.error) === 409
      ? conflictMessage(mut.error, "Could not save the version.")
      : "Could not save the version."
    : null;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{isEdit ? "Edit version" : "Add version"}</DialogTitle>
      <DialogContent>
        <Stack spacing={2.5} sx={{ mt: 1 }}>
          {errorMsg ? <Alert severity="error">{errorMsg}</Alert> : null}
          <TextField
            label="Version number"
            type="number"
            value={versionNumber}
            onChange={(e) => {
              setVersionNumber(e.target.value);
            }}
            fullWidth
            disabled={isEdit}
            error={!numberValid}
            helperText={
              isEdit
                ? "Version number is the natural key and cannot change."
                : "Defaults to the next number; integers ≥ 1."
            }
          />
          <TextField
            label="Name"
            value={versionName}
            onChange={(e) => {
              setVersionName(e.target.value);
            }}
            fullWidth
            slotProps={{ htmlInput: { maxLength: 100 } }}
          />
          <TextField
            label="Release date"
            type="date"
            value={releaseDate}
            onChange={(e) => {
              setReleaseDate(e.target.value);
            }}
            fullWidth
            slotProps={{ inputLabel: { shrink: true } }}
          />
          <TextField
            label="Artifact URI"
            value={artifactUri}
            onChange={(e) => {
              setArtifactUri(e.target.value);
            }}
            fullWidth
            helperText="The stored weights blob. Populated automatically when you upload a file below."
          />

          {/* R0627-08 — weights-file upload. The mint endpoint keys on an
              existing versionNumber, so this is offered in EDIT mode only; in
              create mode the user saves the version first, then uploads. */}
          <Box
            data-testid="version-artifact-upload"
            sx={{
              border: "1px dashed",
              borderColor: "divider",
              borderRadius: 1,
              p: 1.5,
            }}
          >
            <Typography variant="subtitle2" gutterBottom>
              Model weights file
            </Typography>
            {isEdit ? (
              <>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".pt,application/octet-stream"
                  hidden
                  data-testid="version-artifact-file-input"
                  onChange={(e) => {
                    const file = e.target.files?.[0];
                    // Reset so picking the same file again re-fires onChange.
                    e.target.value = "";
                    if (file) void handleFilePicked(file);
                  }}
                />
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<FileUpIcon fontSize="small" />}
                  disabled={uploading || pending}
                  onClick={() => {
                    fileInputRef.current?.click();
                  }}
                >
                  {artifactUri ? "Replace weights file" : "Upload weights file"}
                </Button>
                {uploadedName ? (
                  <Typography
                    variant="caption"
                    color="success.main"
                    sx={{ display: "block", mt: 1 }}
                    data-testid="version-artifact-uploaded"
                  >
                    Uploaded {uploadedName}
                  </Typography>
                ) : artifactUri ? (
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ display: "block", mt: 1 }}
                  >
                    Current: {artifactFileName(artifactUri)}
                  </Typography>
                ) : null}
                {uploading ? (
                  <Box sx={{ mt: 1 }} data-testid="version-artifact-progress">
                    <LinearProgress variant="determinate" value={uploadPct} />
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ mt: 0.5, display: "block" }}
                    >
                      Uploading… {uploadPct}%
                    </Typography>
                  </Box>
                ) : null}
                {uploadError ? (
                  <Alert
                    severity="error"
                    sx={{ mt: 1 }}
                    data-testid="version-artifact-error"
                  >
                    {uploadError}
                  </Alert>
                ) : null}
              </>
            ) : (
              <Typography variant="caption" color="text.secondary">
                Save the version first, then edit it to upload the trained
                weights file (.pt).
              </Typography>
            )}
          </Box>

          <TextField
            label="Note"
            value={note}
            onChange={(e) => {
              setNote(e.target.value);
            }}
            fullWidth
            multiline
            minRows={2}
          />
          <FormControlLabel
            control={
              <Switch
                checked={isActive}
                onChange={(e) => {
                  setIsActive(e.target.checked);
                }}
              />
            }
            label="Active"
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={!canSubmit}
        >
          {isEdit ? "Save" : "Add"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
